import type { SocialAuthProvider } from "@pax/api-contract/client-config";
import { cn, HapticSurface, panelRow, panelRowQuiet, Skeleton } from "@pax/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { ChevronRight, Laptop, LogOut, Smartphone, Trash2 } from "lucide-react";
import { useAccountDeletion } from "@/features/auth/account-deletion";
import { isActingAs } from "@/features/auth/act-as";
import { ProviderMark } from "@/features/auth/components/provider-marks";
import { clientConfigQuery, type Session } from "@/features/auth/queries";
import {
  linkedAccountsQuery,
  PROVIDER_NAMES,
  type SignedInSession,
  sessionDeviceName,
  sessionOnPhone,
  signedInSessionsQuery,
} from "@/features/auth/sign-in-methods";
import { linkProvider } from "@/features/auth/social-sign-in";
import { requestErrorMessage } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { formatTimeAgo } from "@/lib/format";
import { Group, quietButton } from "./parts";

const settingsRoute = getRouteApi("/_authenticated/settings/{-$section}");

/** Where a provider sends the player back to after connecting, whether it worked or not. */
const ACCOUNT_SETTINGS_PATH = "/settings/account";

/** What went wrong when a provider sent the player back with an `error`. */
function connectErrorMessage(code: string): string {
  return code === "account_already_linked_to_different_user"
    ? "That account already belongs to another Pax account."
    : "Connecting didn't finish. Try again.";
}

/**
 * The account itself: its email, the providers it signs in with, each signed-in browser and app,
 * and deleting it. While acting as someone, nothing here can be changed.
 */
export function AccountSection({ session }: { session: Session }) {
  const acting = isActingAs(session);
  return (
    <div className="flex flex-col gap-6">
      <Group label="Email">
        <div className={panelRowQuiet}>
          <span className="min-w-0 flex-1 truncate">{session.user.email}</span>
        </div>
      </Group>
      <SignInMethods locked={acting} />
      <Sessions currentSessionId={session.session.id} locked={acting} />
      {acting ? null : <DeleteAccount />}
    </div>
  );
}

function SignInMethods({ locked }: { locked: boolean }) {
  const queryClient = useQueryClient();
  const { error: returnedError } = settingsRoute.useSearch();
  const { data: clientConfig } = useQuery(clientConfigQuery);
  const linked = useQuery(linkedAccountsQuery);
  const connect = useMutation({
    mutationFn: (provider: SocialAuthProvider) =>
      linkProvider({
        provider,
        callbackURL: ACCOUNT_SETTINGS_PATH,
        errorCallbackURL: ACCOUNT_SETTINGS_PATH,
      }),
  });
  const disconnect = useMutation({
    mutationFn: async (accountId: string) => {
      const { error } = await authClient.unlinkAccount({ accountId });
      if (error) throw new Error(error.message || "Disconnecting didn't finish. Try again.");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: linkedAccountsQuery.queryKey }),
  });

  // Every provider this deployment offers, and any the account still has that it no longer does.
  const providers = [
    ...new Set([
      ...(clientConfig?.socialProviders ?? []),
      ...(linked.data?.map(account => account.provider) ?? []),
    ]),
  ];
  // The last way in can't be disconnected.
  const canDisconnect = (linked.data?.length ?? 0) > 1;
  const failure = disconnect.error ?? connect.error;
  const busy = connect.isPending || disconnect.isPending;

  return (
    <Group
      label="Sign-in methods"
      note={
        failure ? (
          <span role="alert" className="text-destructive">
            {requestErrorMessage(failure)}
          </span>
        ) : returnedError ? (
          <span role="alert" className="text-destructive">
            {connectErrorMessage(returnedError)}
          </span>
        ) : undefined
      }
    >
      {!linked.data || !clientConfig ? (
        <div className={panelRowQuiet}>
          <Skeleton className="size-4 shrink-0 rounded" />
          <Skeleton className="h-4 w-24" />
        </div>
      ) : (
        providers.map(provider => {
          const connected = linked.data.find(account => account.provider === provider);
          return (
            <div key={provider} className={cn(panelRowQuiet, "gap-3")}>
              <ProviderMark provider={provider} />
              <span className="min-w-0 flex-1 truncate">{PROVIDER_NAMES[provider]}</span>
              {locked || (connected && !canDisconnect) ? (
                <span className="text-xs text-muted-foreground">
                  {connected ? "Connected" : "Not connected"}
                </span>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  className={quietButton}
                  onClick={() =>
                    connected ? disconnect.mutate(connected.accountId) : connect.mutate(provider)
                  }
                >
                  {connected ? "Disconnect" : "Connect"}
                </button>
              )}
            </div>
          );
        })
      )}
    </Group>
  );
}

/** When a session was last used. Sessions renew once a day, so it is told to the day. */
function lastActive(updatedAt: Date | string): string {
  const day = new Date(updatedAt);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (day.toDateString() === today.toDateString()) return "Active today";
  if (day.toDateString() === yesterday.toDateString()) return "Active yesterday";
  return `Active ${formatTimeAgo(day.toISOString())}`;
}

function Sessions({ currentSessionId, locked }: { currentSessionId: string; locked: boolean }) {
  const queryClient = useQueryClient();
  const sessions = useQuery(signedInSessionsQuery);
  const refresh = () => queryClient.invalidateQueries({ queryKey: signedInSessionsQuery.queryKey });
  const signOutOne = useMutation({
    mutationFn: async (session: SignedInSession) => {
      const { error } = await authClient.revokeSession({ token: session.token });
      if (error) throw new Error(error.message || "Signing out didn't finish. Try again.");
    },
    onSuccess: refresh,
  });
  const signOutOthers = useMutation({
    mutationFn: async () => {
      const { error } = await authClient.revokeOtherSessions();
      if (error) throw new Error(error.message || "Signing out didn't finish. Try again.");
    },
    onSuccess: refresh,
  });
  const others = sessions.data?.filter(session => session.id !== currentSessionId) ?? [];
  const failure = signOutOne.error ?? signOutOthers.error;
  const busy = signOutOne.isPending || signOutOthers.isPending;

  return (
    <Group
      label="Sessions"
      note={
        failure ? (
          <span role="alert" className="text-destructive">
            {requestErrorMessage(failure)}
          </span>
        ) : undefined
      }
    >
      {!sessions.data ? (
        <div className={cn(panelRowQuiet, "h-auto min-h-11 gap-3 py-2")}>
          <Skeleton className="size-4 shrink-0 rounded" />
          <Skeleton className="h-4 w-32" />
        </div>
      ) : (
        sessions.data.map(session => {
          const current = session.id === currentSessionId;
          const Icon = sessionOnPhone(session) ? Smartphone : Laptop;
          return (
            <div key={session.id} className={cn(panelRowQuiet, "h-auto min-h-11 gap-3 py-2")}>
              <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{sessionDeviceName(session)}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {current ? "This session" : lastActive(session.updatedAt)}
                </span>
              </span>
              {current || locked ? null : (
                <button
                  type="button"
                  disabled={busy}
                  className={quietButton}
                  onClick={() => signOutOne.mutate(session)}
                >
                  Sign out
                </button>
              )}
            </div>
          );
        })
      )}
      {others.length > 1 && !locked ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => signOutOthers.mutate()}
          className={cn(
            panelRow,
            "text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50",
          )}
        >
          <LogOut aria-hidden className="size-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-left">Sign out of all other sessions</span>
          <HapticSurface />
        </button>
      ) : null}
    </Group>
  );
}

function DeleteAccount() {
  const { openAccountDeletion } = useAccountDeletion();
  return (
    <Group>
      <button
        type="button"
        onClick={() => openAccountDeletion()}
        className={cn(panelRow, "text-muted-foreground hover:text-foreground")}
      >
        <Trash2 aria-hidden className="size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate text-left">Delete account</span>
        <ChevronRight aria-hidden className="size-4 shrink-0 opacity-60" />
        <HapticSurface />
      </button>
    </Group>
  );
}
